import { SearchFieldCollectionMappings, SearchFieldId } from "./Constants"
import { TypesenseContextProps } from "./TypesenseProvider"

// V2 Implementation with hierarchical structure
export type UserContext = "exports" | "transfers" | "generic"

export interface KeySelectionOptions {
  collectionId?: SearchFieldId
  searchApiKey?: string
  multiCollectionKeysV2?: TypesenseContextProps["multiCollectionKeysV2"]
  usePermissions?: boolean
  collectionName?: string
  context?: UserContext
}

export function getTypesenseKey(
  typesenseKeyOptions: KeySelectionOptions,
): string | undefined {
  const {
    collectionId,
    multiCollectionKeysV2,
    usePermissions,
    collectionName,
    context = "generic",
    searchApiKey,
  } = typesenseKeyOptions

  const collection =
    collectionName ??
    (collectionId ? SearchFieldCollectionMappings[collectionId] : undefined)

  const isPrivateCollection = collection?.startsWith("private:")

  if (!isPrivateCollection) {
    return searchApiKey
  }

  const privateCollection = collection?.replace("private:", "")

  if (!privateCollection) return undefined

  return selectV2Key(
    privateCollection,
    multiCollectionKeysV2,
    context,
    usePermissions,
  )
}

export function selectV2Key(
  collection: string,
  keys: TypesenseContextProps["multiCollectionKeysV2"],
  context: UserContext,
  usePermissions?: boolean,
): string | undefined {
  if (!keys) return undefined

  switch (collection) {
    // Generic collections - use root level keys
    case "products":
      return context == "generic" ? keys.products : undefined
    case "exporters":
      return context == "generic" ? keys.exporters : undefined
    case "agency":
      return context == "generic" ? keys.agency : undefined

    // Context-aware collections
    case "export-certificates":
    case "export-requests":
      return context === "exports"
        ? keys.exports?.exportCertificates
        : undefined

    case "transfer-requests":
      if (context === "exports") {
        return keys.exports?.transferRequests // Cross-role access
      } else if (context === "transfers") {
        return keys.transfers?.transferRequests
      }
      return undefined

    case "registration":
    case "registrations": {
      const registrationKey = usePermissions
        ? "registration"
        : "registrationAll"
      if (context === "exports") {
        return keys.exports?.[registrationKey]
      } else if (context === "transfers") {
        return keys.transfers?.[registrationKey]
      }
      return undefined
    }

    case "supporting-documents":
      if (context === "exports") {
        return keys.exports?.supportingDocuments
      } else if (context === "transfers") {
        return keys.transfers?.supportingDocuments
      }
      return undefined

    default:
      return keys.partyApiKey
  }
}
